// Cheap NDC-projection hover cue: paints the canvas cursor as 'pointer' when
// the mouse is within a few sphere radii of the selection center. Single
// pointermove listener; no per-frame raycast. Independent of the drag-state
// machine so it can run on a headless setup.
//
// The hover hit-test here intentionally duplicates the one in interactions.ts
// (this is the always-on, drag-machine-independent path). Both call
// setMarkerHover with the same value — last write wins — pending a future
// consolidation onto a single pointermove source. The vertical-handle zone is
// folded in here too so this mousemove listener (which fires after the pointer
// one and also sets the cursor) doesn't clear the pointer cursor over the knob;
// setHandleHover stays owned by interactions.ts.

import * as THREE from 'three';
import type { ViewerContext } from '@app/context';
import {
    setMarkerHover, getHandleWorldPosition, verticalDragAllowed,
    getHorizontalHandleWorldPosition, horizontalDragAxis, HORIZONTAL_MIN_ELEV_DEG,
    cameraElevationDeg,
} from './marker';

export function setupSelectionHoverCursor(viewer: ViewerContext): () => void {
    const renderer = viewer.three.renderer;
    if (!renderer || !renderer.domElement) return () => {};

    const worldCenter = new THREE.Vector3();
    const edgeWorld = new THREE.Vector3();
    const right = new THREE.Vector3();
    const ndcCenter = new THREE.Vector3();
    const ndcEdge = new THREE.Vector3();
    const handleWorld = new THREE.Vector3();
    const handleNdc = new THREE.Vector3();
    const hHandleWorld = new THREE.Vector3();
    const hHandleNdc = new THREE.Vector3();

    const onPointerMove = (ev: MouseEvent): void => {
        const dot = viewer.objects.selection.centerDot;
        if (!dot) return;
        const camera = viewer.three.camera;
        if (!camera) return;
        const rect = renderer.domElement.getBoundingClientRect();
        dot.getWorldPosition(worldCenter);
        ndcCenter.copy(worldCenter).project(camera);
        const centerPxX = (ndcCenter.x * 0.5 + 0.5) * rect.width;
        const centerPxY = (-ndcCenter.y * 0.5 + 0.5) * rect.height;
        right.set(1, 0, 0).applyQuaternion(camera.quaternion).normalize();
        const geom = dot.geometry;
        const baseRadius = (geom && geom.boundingSphere) ? geom.boundingSphere.radius : 0.08;
        const radiusWorld = baseRadius * dot.scale.x;
        edgeWorld.copy(worldCenter).addScaledVector(right, radiusWorld);
        ndcEdge.copy(edgeWorld).project(camera);
        const dxPx = (ndcEdge.x - ndcCenter.x) * 0.5 * rect.width;
        const dyPx = (ndcEdge.y - ndcCenter.y) * -0.5 * rect.height;
        const radiusPx = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
        const mx = ev.clientX - rect.left;
        const my = ev.clientY - rect.top;
        const distPx = Math.hypot(mx - centerPxX, my - centerPxY);
        const targetPx = Math.max(26, radiusPx * 2.0);
        const overCore = distPx <= targetPx;
        // The whole stem (center→knob) is grabbable at the same target radius;
        // the selection circle takes precedence where they overlap.
        let overHandle = false;
        if (!overCore && verticalDragAllowed(camera) && getHandleWorldPosition(handleWorld)) {
            handleNdc.copy(handleWorld).project(camera);
            const hx = (handleNdc.x * 0.5 + 0.5) * rect.width;
            const hy = (-handleNdc.y * 0.5 + 0.5) * rect.height;
            const abx = hx - centerPxX;
            const aby = hy - centerPxY;
            const len2 = abx * abx + aby * aby;
            let t = len2 > 0 ? ((mx - centerPxX) * abx + (my - centerPxY) * aby) / len2 : 0;
            if (t < 0) t = 0; else if (t > 1) t = 1;
            overHandle = Math.hypot(mx - (centerPxX + t * abx), my - (centerPxY + t * aby)) <= targetPx;
        }
        // ↔ stem: live only in the equatorial regime with a clear cross-screen axis.
        let overHHandle = false;
        const equatorial = cameraElevationDeg(camera) < HORIZONTAL_MIN_ELEV_DEG;
        if (!overCore && !overHandle && equatorial && horizontalDragAxis(camera) !== null
            && getHorizontalHandleWorldPosition(hHandleWorld)) {
            hHandleNdc.copy(hHandleWorld).project(camera);
            const hx = (hHandleNdc.x * 0.5 + 0.5) * rect.width;
            const hy = (-hHandleNdc.y * 0.5 + 0.5) * rect.height;
            const abx = hx - centerPxX;
            const aby = hy - centerPxY;
            const len2 = abx * abx + aby * aby;
            let t = len2 > 0 ? ((mx - centerPxX) * abx + (my - centerPxY) * aby) / len2 : 0;
            if (t < 0) t = 0; else if (t > 1) t = 1;
            overHHandle = Math.hypot(mx - (centerPxX + t * abx), my - (centerPxY + t * aby)) <= targetPx;
        }
        const hovering = overCore || overHandle || overHHandle;
        setMarkerHover(overCore);
        renderer.domElement.style.cursor = hovering ? 'pointer' : '';
    };

    renderer.domElement.addEventListener('mousemove', onPointerMove);
    return () => { try { renderer.domElement.removeEventListener('mousemove', onPointerMove); } catch { /* swallow */ } };
}
